import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { adminApi } from '../services/api';

interface AdminReport {
  report_id: string;
  target_type: string;
  target_id: string;
  reporter_user_id: string;
  reason: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  processed_at?: string;
}

interface ReportsResponse {
  reports: AdminReport[];
  count: number;
}

const AdminReports: React.FC = () => {
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // 処理ダイアログ状態
  const [processDialog, setProcessDialog] = useState<{
    open: boolean;
    report: AdminReport | null;
  }>({ open: false, report: null });
  
  const [action, setAction] = useState<'approve' | 'reject'>('approve');
  const [adminNote, setAdminNote] = useState('');
  const [processLoading, setProcessLoading] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params: any = { status: statusFilter, limit: 50 };
      
      const response: ReportsResponse = await adminApi.getReports(params);
      setReports(response.reports);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || '通報一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessReport = async () => {
    if (!processDialog.report) return;

    try {
      setProcessLoading(true);
      await adminApi.processReport(processDialog.report.report_id, action, adminNote);
      
      setProcessDialog({ open: false, report: null });
      setAdminNote('');
      await fetchReports();
    } catch (err: any) {
      setError(err.response?.data?.error || '通報の処理に失敗しました');
    } finally {
      setProcessLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="未処理" color="warning" size="small" />;
      case 'approved':
        return <Chip label="承認済み" color="success" size="small" />;
      case 'rejected':
        return <Chip label="却下済み" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const getTargetTypeLabel = (targetType: string) => {
    switch (targetType) {
      case 'review':
        return '口コミ';
      case 'user':
        return 'ユーザー';
      case 'blog':
        return 'ブログ記事';
      default:
        return targetType;
    }
  };

  const getReasonLabel = (reason: string) => {
    const reasonMap: Record<string, string> = {
      'inappropriate_content': '不適切な内容',
      'spam': 'スパム',
      'false_information': '虚偽の情報',
      'harassment': 'ハラスメント',
      'copyright_violation': '著作権侵害',
      'other': 'その他'
    };
    return reasonMap[reason] || reason;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        通報管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box mb={2}>
        <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
          <InputLabel>ステータス</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="pending">未処理</MenuItem>
            <MenuItem value="approved">承認済み</MenuItem>
            <MenuItem value="rejected">却下済み</MenuItem>
            <MenuItem value="all">すべて</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>対象タイプ</TableCell>
              <TableCell>対象ID</TableCell>
              <TableCell>通報者</TableCell>
              <TableCell>理由</TableCell>
              <TableCell>説明</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>通報日</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.report_id}>
                <TableCell>{getTargetTypeLabel(report.target_type)}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {report.target_id.substring(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {report.reporter_user_id.substring(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>{getReasonLabel(report.reason)}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      maxWidth: 200, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {report.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(report.status)}</TableCell>
                <TableCell>
                  {new Date(report.created_at).toLocaleDateString('ja-JP')}
                </TableCell>
                <TableCell>
                  {report.status === 'pending' && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setProcessDialog({ open: true, report })}
                    >
                      処理
                    </Button>
                  )}
                  {report.status !== 'pending' && (
                    <Typography variant="caption" color="text.secondary">
                      {report.processed_at ? 
                        new Date(report.processed_at).toLocaleDateString('ja-JP') : 
                        '処理済み'
                      }
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {reports.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            {statusFilter === 'pending' ? '未処理の通報はありません' : '該当する通報はありません'}
          </Typography>
        </Box>
      )}

      {/* 通報処理ダイアログ */}
      <Dialog 
        open={processDialog.open} 
        onClose={() => setProcessDialog({ open: false, report: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>通報処理</DialogTitle>
        <DialogContent>
          {processDialog.report && (
            <Box>
              <Typography variant="h6" gutterBottom>
                通報詳細
              </Typography>
              <Box mb={2}>
                <Typography variant="body2"><strong>対象:</strong> {getTargetTypeLabel(processDialog.report.target_type)}</Typography>
                <Typography variant="body2"><strong>対象ID:</strong> {processDialog.report.target_id}</Typography>
                <Typography variant="body2"><strong>通報者:</strong> {processDialog.report.reporter_user_id}</Typography>
                <Typography variant="body2"><strong>理由:</strong> {getReasonLabel(processDialog.report.reason)}</Typography>
                <Typography variant="body2"><strong>説明:</strong> {processDialog.report.description || '-'}</Typography>
                <Typography variant="body2"><strong>通報日:</strong> {new Date(processDialog.report.created_at).toLocaleString('ja-JP')}</Typography>
              </Box>

              <FormControl fullWidth margin="normal">
                <InputLabel>処理アクション</InputLabel>
                <Select
                  value={action}
                  onChange={(e) => setAction(e.target.value as 'approve' | 'reject')}
                >
                  <MenuItem value="approve">承認 (通報内容が正当)</MenuItem>
                  <MenuItem value="reject">却下 (通報内容が不当)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="管理者メモ"
                multiline
                rows={3}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                margin="normal"
                placeholder="処理理由や詳細を記入してください"
              />

              {action === 'approve' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  承認すると、対象コンテンツが削除または非公開になる場合があります。
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialog({ open: false, report: null })}>
            キャンセル
          </Button>
          <Button 
            onClick={handleProcessReport} 
            disabled={processLoading}
            color="primary"
            variant="contained"
          >
            {processLoading ? <CircularProgress size={20} /> : '処理実行'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReports;
